import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { faixaPessoa } from '../dashboard/rotina';
import MissoesRodizio from '../dashboard/MissoesRodizio';

// Rodízio diário (backend routes/rodizio.py, Adnéia 16/10/2026): no primeiro login do
// dia a pessoa vê as demandas dela; o botão "Minhas demandas" reabre a qualquer hora.
// Para a gestão o mesmo botão mostra o rodízio de hoje e marca quem faltou.

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TIPOS = [
  { key: 'canais', titulo: 'Canais' },
  { key: 'limpeza', titulo: 'Limpeza' },
  { key: 'atividades', titulo: 'Demais atividades' },
];

// A cor é da PESSOA, não da lista (Adnéia, 17/10/2026): Caio sempre azul, Gabriela sempre
// laranja, mesmo no dia em que a lista dela é a azul. O que é da gestão fica roxo.
const COR_SEM_DONO = 'bg-violet-50 border-violet-200 text-violet-900 dark:bg-violet-950/30 dark:border-violet-800 dark:text-violet-100';

export const juntarNomes = (nomes, max = 6) => {
  const lista = nomes.slice(0, max);
  const resto = nomes.length - lista.length;
  if (resto > 0) return `${lista.join(', ')} e mais ${resto}`;
  if (lista.length <= 1) return lista.join('');
  return `${lista.slice(0, -1).join(', ')} e ${lista[lista.length - 1]}`;
};

const Chip = ({ item, mostrarStatus, dono }) => {
  const feito = mostrarStatus && item.feito;
  const base = feito
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-200'
    : (item.lista === 'sem_dono' ? COR_SEM_DONO : faixaPessoa(dono));
  const titulo = feito
    ? `Feito${item.por ? ' — ' + item.por : ''}`
    : (item.em_aberto ? `${item.em_aberto} pendente(s)` : '');
  return (
    <span title={titulo} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border ${base}`}>
      {feito && <span aria-hidden="true">✓</span>}
      {item.nome}
      {item.em_aberto > 0 && !feito && <span className="opacity-70">({item.em_aberto})</span>}
      {item.acionar_hoje && !feito && (
        <span className="ml-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-300">acionar hoje</span>
      )}
    </span>
  );
};

export const GruposDemandas = ({ itens, mostrarStatus, dono }) => (
  <div className="space-y-3">
    {TIPOS.map(({ key, titulo }) => {
      const lista = (itens && itens[key]) || [];
      if (!lista.length) return null;
      return (
        <div key={key}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{titulo}</p>
          <div className="flex flex-wrap gap-1.5">
            {lista.map((it) => <Chip key={key + it.lista + it.nome} item={it} mostrarStatus={mostrarStatus} dono={dono} />)}
          </div>
        </div>
      );
    })}
  </div>
);

const MinhasDemandas = () => {
  const { user, getAuthHeader, isConsulta, isDashboardOnly } = useAuth();
  const [dados, setDados] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [boasVindas, setBoasVindas] = useState(false);
  const [salvando, setSalvando] = useState('');
  const ehGestao = user?.role === 'admin';

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/rodizio/hoje`, { headers: getAuthHeader() });
      setDados(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (!user || isConsulta || isDashboardOnly) return;
    carregar().then((d) => {
      if (d && d.ativo && d.mostrar_boas_vindas) {
        setBoasVindas(true);
        setAberto(true);
      }
    });
  }, [user, isConsulta, isDashboardOnly, carregar]);

  const mudarAberto = (v) => {
    setAberto(v);
    if (v) { carregar(); return; }
    if (boasVindas) {
      setBoasVindas(false);
      axios.post(`${API_URL}/api/rodizio/boas-vindas`, {}, { headers: getAuthHeader() }).catch(() => {});
    }
  };

  const marcarFalta = async (email, ausente) => {
    setSalvando(email);
    try {
      await axios.post(`${API_URL}/api/rodizio/ausencia`, { email, ausente }, { headers: getAuthHeader() });
      await carregar();
      toast.success(ausente ? 'Falta marcada: a lista passou para quem veio.' : 'Falta desmarcada.');
    } catch (e) {
      toast.error('Não salvou: ' + (e?.response?.data?.detail || e?.message || ''));
    } finally {
      setSalvando('');
    }
  };

  if (!user || isConsulta || isDashboardOnly || !dados) return null;
  if (!dados.ativo && !ehGestao) return null;

  const eu = dados.eu || {};
  const nomeColegaAusente = (dados.ausentes || []).find((a) => a.email !== eu.email);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => mudarAberto(true)} className="gap-1.5" data-testid="btn-minhas-demandas">
        <ClipboardList className="h-4 w-4" />
        <span className="hidden md:inline">{ehGestao ? 'Rodízio' : 'Minhas demandas'}</span>
      </Button>

      <Dialog open={aberto} onOpenChange={mudarAberto}>
        {/* Missões do dia nesta janela (Adnéia, 17/10/2026): a gestão vê os três cartões
            (janela larga); cada atendente, só o seu. A boas-vindas segue com as fichas. */}
        <DialogContent className={`${ehGestao ? 'max-w-6xl' : (boasVindas ? 'max-w-xl' : 'max-w-2xl')} max-h-[85vh] overflow-y-auto`}>
          {!dados.ativo ? (
            <>
              <DialogHeader>
                <DialogTitle>Rodízio do atendimento</DialogTitle>
                <DialogDescription>{dados.motivo}</DialogDescription>
              </DialogHeader>
              {ehGestao && <MissoesRodizio />}
              <DialogFooter><Button onClick={() => mudarAberto(false)}>Fechar</Button></DialogFooter>
            </>
          ) : ehGestao ? (
            <>
              <DialogHeader>
                <DialogTitle>Rodízio de hoje ({dados.data_br})</DialogTitle>
                <DialogDescription>
                  Quem está com cada lista e o que ainda está aberto.{dados.previa ? ' Ninguém da equipe abriu o ELO hoje ainda.' : ''}
                </DialogDescription>
              </DialogHeader>
              <MissoesRodizio />
              <div className="space-y-3 border-t pt-3">
                <p className="text-sm font-semibold">Quem veio hoje</p>
                {(dados.gestao?.pessoas || []).map((p) => (
                  <div key={p.email} className={`rounded-lg border p-3 ${p.ausente ? 'opacity-70' : ''}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-sm">
                        <strong>{p.nome}</strong>
                        <span className="text-muted-foreground"> · lista {p.lista_do_dia}</span>
                        {p.ausente && <span className="ml-2 text-xs font-semibold text-amber-700 dark:text-amber-300">não veio hoje</span>}
                        {!p.ausente && p.listas.length > 1 && <span className="ml-2 text-xs text-muted-foreground">(com as duas listas)</span>}
                      </div>
                      <Button size="sm" variant="outline" disabled={salvando === p.email}
                        onClick={() => marcarFalta(p.email, !p.ausente)}>
                        {p.ausente ? 'Veio, desmarcar falta' : 'Não veio hoje'}
                      </Button>
                    </div>
                    {!p.ausente && (
                      <p className="text-xs text-muted-foreground mt-1.5">
                        {p.abertas.length
                          ? `Aberto (${p.abertas.length} de ${p.total}): ${juntarNomes(p.abertas.map((a) => a.nome), 8)}`
                          : `Tudo finalizado (${p.total}).`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter><Button onClick={() => mudarAberto(false)}>Fechar</Button></DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">
                  {boasVindas ? `Olá ${eu.primeiro_nome}, vamos começar mais um dia?` : `${eu.primeiro_nome}, suas missões de hoje`}
                </DialogTitle>
                <DialogDescription>{boasVindas ? 'Essas são suas demandas de hoje:' : 'O que você aciona, encerra e confere hoje.'}</DialogDescription>
              </DialogHeader>
              {nomeColegaAusente && (
                <p className="text-sm rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2">
                  Hoje {nomeColegaAusente.apelido || nomeColegaAusente.nome} não veio: as demandas dessa lista também estão com você.
                </p>
              )}
              {boasVindas
                ? <GruposDemandas itens={eu.itens} mostrarStatus={false} dono={eu.nome} />
                : <MissoesRodizio somente={eu.nome} />}
              <p className="text-sm border-t pt-3">Se precisar, é só me avisar. Bom trabalho. Ad.</p>
              <DialogFooter><Button onClick={() => mudarAberto(false)}>{boasVindas ? 'Começar' : 'Fechar'}</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MinhasDemandas;
